import { lazy, Suspense, useEffect, useState } from "react";
import { AppShell, GestureArea, Screen, Skeleton } from "./ui";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { EntityNotFound } from "./components/EntityNotFound";
import { SignInGate } from "./components/SignInGate";
import { Journal } from "./screens/Journal";
import { applyTheme, getTheme } from "./lib/theme";
import { getLocale } from "./lib/i18n";
import { useAcademyStartup } from "./lib/learn/startup";
import { back, navigate, useRoute } from "./lib/router";
import { startSessionWatch } from "./lib/social/auth";
import { startNotificationRouteWatch } from "./lib/social/inboxBadge";
import { startPresenceBeacon } from "./lib/social/presenceBeacon";
import { startDraftSync } from "./lib/tasting/draftSync";
import { edgeWineIdFromSegment, entityIdFromSegment } from "./lib/wineIdentity";

// The landing journal is the one screen in the entry bundle; every other screen is its own chunk,
// so a first visit downloads only what it paints. The service worker precaches the chunks the
// app must open offline (vite.config.ts) and caches the rest as they are visited.
const Install = lazy(() => import("./screens/Install").then((m) => ({ default: m.Install })));
const AddWine = lazy(() => import("./screens/AddWine").then((m) => ({ default: m.AddWine })));
const CellarAdd = lazy(() => import("./screens/CellarAdd").then((m) => ({ default: m.CellarAdd })));
const CellarItemDetail = lazy(() =>
  import("./screens/CellarItemDetail").then((m) => ({ default: m.CellarItemDetail })),
);
const CellarList = lazy(() =>
  import("./screens/CellarList").then((m) => ({ default: m.CellarList })),
);
const FlightScreen = lazy(() =>
  import("./screens/FlightScreen").then((m) => ({ default: m.FlightScreen })),
);
const HostFlight = lazy(() =>
  import("./screens/HostFlight").then((m) => ({ default: m.HostFlight })),
);
const AppellationDetail = lazy(() =>
  import("./screens/AppellationDetail").then((m) => ({ default: m.AppellationDetail })),
);
const EdgeWine = lazy(() => import("./screens/EdgeWine").then((m) => ({ default: m.EdgeWine })));
const JoinFlight = lazy(() =>
  import("./screens/JoinFlight").then((m) => ({ default: m.JoinFlight })),
);
const LabelPrint = lazy(() =>
  import("./screens/LabelPrint").then((m) => ({ default: m.LabelPrint })),
);
const LearnRoute = lazy(() =>
  import("./screens/LearnRoute").then((m) => ({ default: m.LearnRoute })),
);
const TastingFlow = lazy(() =>
  import("./screens/Tasting").then((m) => ({ default: m.TastingFlow })),
);
const NoteNew = lazy(() => import("./screens/Tasting").then((m) => ({ default: m.NoteNew })));
const Redirect = lazy(() => import("./screens/Tasting").then((m) => ({ default: m.Redirect })));
const Profile = lazy(() => import("./screens/Profile").then((m) => ({ default: m.Profile })));
const Result = lazy(() => import("./screens/Result").then((m) => ({ default: m.Result })));
const RevealTasting = lazy(() =>
  import("./screens/RevealTasting").then((m) => ({ default: m.RevealTasting })),
);
const Scan = lazy(() => import("./screens/Scan").then((m) => ({ default: m.Scan })));
const Search = lazy(() => import("./screens/Search").then((m) => ({ default: m.Search })));
const TastingDetail = lazy(() =>
  import("./screens/TastingDetail").then((m) => ({ default: m.TastingDetail })),
);
const WineDetail = lazy(() =>
  import("./screens/WineDetail").then((m) => ({ default: m.WineDetail })),
);
const WineryDetail = lazy(() =>
  import("./screens/WineryDetail").then((m) => ({ default: m.WineryDetail })),
);
const Friends = lazy(() => import("./screens/Friends").then((m) => ({ default: m.Friends })));
const GroupDirectory = lazy(() =>
  import("./screens/GroupDirectory").then((m) => ({ default: m.GroupDirectory })),
);
const Groups = lazy(() => import("./screens/Groups").then((m) => ({ default: m.Groups })));
const GroupDetail = lazy(() =>
  import("./screens/GroupDetail").then((m) => ({ default: m.GroupDetail })),
);
const GroupShop = lazy(() => import("./screens/GroupShop").then((m) => ({ default: m.GroupShop })));
const GroupShopSetup = lazy(() =>
  import("./screens/GroupShopSetup").then((m) => ({ default: m.GroupShopSetup })),
);
const SessionDetail = lazy(() =>
  import("./screens/SessionDetail").then((m) => ({ default: m.SessionDetail })),
);
const Activity = lazy(() => import("./screens/Activity").then((m) => ({ default: m.Activity })));
const Sessions = lazy(() => import("./screens/Sessions").then((m) => ({ default: m.Sessions })));
const SignIn = lazy(() => import("./screens/SignIn").then((m) => ({ default: m.SignIn })));
const Privacy = lazy(() => import("./screens/Privacy").then((m) => ({ default: m.Privacy })));
const InviteLanding = lazy(() =>
  import("./screens/InviteLanding").then((m) => ({ default: m.InviteLanding })),
);
const Samen = lazy(() => import("./screens/Samen").then((m) => ({ default: m.Samen })));
const MyProfile = lazy(() => import("./screens/MyProfile").then((m) => ({ default: m.MyProfile })));

// Full-bleed / modal-like flows hide the bottom tab bar; every other route
// (including "view" detail screens like wine/:id, cellar/:id, taste/:id)
// keeps it, so navigating away never requires backing out first. This is
// the ONE place that decides tab-bar visibility -- a route branch below
// must never set hideTabBar itself, or the two can drift out of sync again.
const FULL_BLEED_ROUTES: ReadonlyArray<(segs: readonly string[]) => boolean> = [
  (s) => s[0] === "scan",
  (s) => s[0] === "add-wine",
  (s) => s[0] === "tasting",
  (s) => s[0] === "note" && (s[1] === "new" || s[1] === "blind"),
  (s) => s[0] === "taste" && s[1] !== undefined && s[2] === "reveal",
  (s) => s[0] === "cellar" && s[1] === "add",
  (s) => s[0] === "cellar" && s[1] !== undefined && s[2] === "print",
  (s) => s[0] === "host",
  (s) => s[0] === "join",
  (s) => s[0] === "flight" && s[1] !== undefined,
  // A lesson, the drills and the sitting keep their action bar the lowest thing under the thumb.
  (s) => s[0] === "learn" && (s[1] === "lesson" || s[1] === "drills" || s[1] === "prove"),
  // A running sitting is the whole screen: the clock and the hand-in button are all there is.
  (s) => s[0] === "learn" && s[1] === "paper" && s[3] === "sitting",
];

/** What a route shows while its screen's chunk loads: the same card skeleton the screens use. */
function RouteLoading(): React.JSX.Element {
  return (
    <Screen aria-busy={true} data-testid="route-loading">
      <Skeleton kind="card" finite />
    </Screen>
  );
}

/** Wide enough for a thumb resting on the bezel, narrow enough that Pager and SwipeRow areas keep their own swipes. */
const EDGE_ZONE_PX = 24;

/** True while a Sheet is open: the edge swipe must not navigate behind a modal. */
const sheetOpen = (): boolean =>
  document.querySelector('[role="dialog"][aria-modal="true"]') !== null;

/**
 * Swiping right from the left screen edge goes back, on touch screens only. The back button (and
 * the browser's own Alt+Left) is the twin, so the gesture is never the only way.
 */
function EdgeBack({ fallback }: { fallback: string }): React.JSX.Element {
  return (
    <GestureArea
      axis="x"
      position="fixed"
      top={0}
      bottom={0}
      left={0}
      width={EDGE_ZONE_PX}
      zIndex="4"
      display={{ default: "none", touch: "flex" }}
      aria-hidden
      onSwipe={{
        right: () => {
          if (!sheetOpen()) back(fallback);
        },
      }}
    />
  );
}

export function App(): React.JSX.Element {
  const route = useRoute();
  useAcademyStartup();
  // Back only means something once the person has moved around inside the app; a first screen has
  // nothing of ours to return to.
  const [moved, setMoved] = useState(false);
  const here = route.segments.join("/");
  const [initial] = useState(here);
  useEffect(() => {
    if (here !== initial) setMoved(true);
  }, [here, initial]);
  const hideTabBar = FULL_BLEED_ROUTES.some((test) => test(route.segments));

  useEffect(() => {
    applyTheme(getTheme());
    document.documentElement.lang = getLocale();
  }, []);

  // Only SignIn's own OAuth-callback effect ever called refreshSession before (auth.ts),
  // so any boot that doesn't land on ?auth=success -- a fresh tab, a reload on /about -- left
  // useSession() stuck at its initial "loading" state forever. One check at boot keeps every
  // signed-in-status screen live without needing a full reload; a check that gets no answer keeps
  // the remembered account and is retried when the app is next in view or online.
  useEffect(() => startSessionWatch(), []);

  // "Open to taste together": reports while visible and signed in, never while hidden or off.
  useEffect(() => startPresenceBeacon(), []);

  // Unfinished tastings follow a signed-in person to their other devices, but only once they turned that on.
  useEffect(() => startDraftSync(), []);

  // A tick on the wine list follows the member to the groups she shares it with, and what waited
  // while offline goes once the device can.
  useEffect(() => {
    let stop: (() => void) | undefined;
    let cancelled = false;
    import("./lib/social/groupShop")
      .then((module) => {
        if (!cancelled) stop = module.startGroupShopMirror();
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stop?.();
    };
  }, []);

  // A tapped social notification moves an already open window to its item.
  useEffect(() => startNotificationRouteWatch((path) => navigate(path)), []);

  // The reconciler keeps the same <main> DOM node across route changes
  // (every route renders inside the one AppShell below, only its children
  // differ), so nothing about mounting moves focus on its own -- do it
  // explicitly whenever the route itself changes.
  useEffect(() => {
    document.querySelector<HTMLElement>("[data-app-main]")?.focus({ preventScroll: true });
    // A compositor-only enter animation instead of a View Transition: it never holds the
    // render or ignores taps, and a reduced-motion user gets none.
    const content = document.querySelector<HTMLElement>("[data-app-content]");
    if (
      content !== null &&
      typeof content.animate === "function" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      content.animate(
        [
          { opacity: 0, transform: "translateY(8px)" },
          { opacity: 1, transform: "none" },
        ],
        { duration: 180, easing: "ease-out" },
      );
    }
  }, [route.segments.join("/")]);

  return (
    <AppShell hideTabBar={hideTabBar}>
      {moved ? <EdgeBack fallback="/" /> : null}
      <ChunkErrorBoundary resetKey={route.path}>
        <Suspense fallback={<RouteLoading />}>{screenFor(route)}</Suspense>
      </ChunkErrorBoundary>
    </AppShell>
  );
}

function screenFor(route: ReturnType<typeof useRoute>): React.JSX.Element {
  const [first, second, third] = route.segments;

  if (first === "scan") return <Scan />;
  if (first === "add-wine") return <AddWine />;
  if (first === "result") return <Result />;
  // Catalogue pages: an id that is neither a catalogue id nor a wine of the user's own (junk) is a
  // not-found state, never another screen. The route name alone picks the page.
  if ((first === "wine" || first === "winery" || first === "appellation") && second !== undefined) {
    const id = entityIdFromSegment(second);
    // A wine only the edge knows (one the user typed in) has no catalogue id but does have a page.
    const edgeId = first === "wine" ? edgeWineIdFromSegment(second) : undefined;
    if (edgeId !== undefined) return <EdgeWine key={edgeId} id={edgeId} />;
    if (id === undefined) return <EntityNotFound />;
    if (first === "wine") return <WineDetail id={id} />;
    if (first === "winery") return <WineryDetail id={id} />;
    return <AppellationDetail id={id} />;
  }
  if (first === "search") return <Search />;
  if (first === "install") return <Install />;
  // Social: friends, groups, live tasting sessions (ticket 302) -- all above
  // the device-id-only anonymous flow, reached from the Samen tab. Each needs an account, so
  // a signed-out visitor gets a sign-in prompt that comes back here instead of a raw error.
  const gated = (screen: React.JSX.Element): React.JSX.Element => (
    <SignInGate
      redirect={`/${route.segments.join("/")}${route.params.size === 0 ? "" : `?${route.params.toString()}`}`}
    >
      {screen}
    </SignInGate>
  );
  // Samen and its profile handle signing out themselves: the settings drawer must open signed out.
  if (first === "samen" && second === "profile") return <MyProfile />;
  if (first === "samen" && second === "activity") return gated(<Activity />);
  if (first === "samen" && second === "sessions") return gated(<Sessions />);
  if (first === "samen") return <Samen />;
  // The About tab moved into the profile's settings drawer; its old address opens that drawer.
  if (first === "about") return <MyProfile settingsOpen />;
  if (first === "signin") return <SignIn redirectTo={route.params.get("redirect") ?? undefined} />;
  // Not gated: the notice is read before signing in as much as after.
  if (first === "privacy") return <Privacy />;
  // Not gated: a signed-out invitee sees who invited them before deciding to sign in.
  if (first === "invite") return <InviteLanding token={second} />;

  if (first === "friends") return gated(<Friends />);
  // Registered before "groups/:id" so the literal "directory" segment wins over the dynamic
  // group-id route (mirrors apps/edge/src/groups.ts's own "/v1/groups/search" vs. "/:id" order).
  if (first === "groups" && second === "directory") return gated(<GroupDirectory />);
  if (first === "groups" && second !== undefined && third === "shop") {
    return route.segments[3] === "setup"
      ? gated(<GroupShopSetup groupId={second} />)
      : gated(<GroupShop groupId={second} />);
  }
  if (first === "groups" && second !== undefined) return gated(<GroupDetail groupId={second} />);
  if (first === "groups") return gated(<Groups />);
  if (first === "session" && second !== undefined) {
    return gated(<SessionDetail sessionId={second} />);
  }

  // Tasting notes
  if (first === "tasting") return <TastingFlow />;
  if (first === "note" && second === "new") return <NoteNew />;
  if (first === "note" && second === "blind") return <Redirect to="/tasting/new?blind=1" />;
  if (first === "taste" && second !== undefined && third === "reveal") {
    return <RevealTasting tastingId={second} />;
  }
  if (first === "taste" && second !== undefined) return <TastingDetail id={second} />;
  if (first === "taste") return <Journal />;

  // Cellar
  if (first === "cellar" && second === "add") return <CellarAdd />;
  if (first === "cellar" && second !== undefined && third === "print") {
    return <LabelPrint id={second} />;
  }
  if (first === "cellar" && second !== undefined) return <CellarItemDetail id={second} />;
  if (first === "cellar") return <CellarList />;

  // Palate profile & recommendations (PRD 4.5)
  if (first === "profile") return <Profile />;

  // Learn
  if (first === "learn") return <LearnRoute segments={route.segments} />;

  // Hosting and joining flights
  if (first === "host") {
    // A flight made from a lesson or a template names it by id.
    const from =
      second === "from-lesson" && third !== undefined && route.segments[3] !== undefined
        ? { kind: "lesson" as const, lessonId: third, stepId: Number(route.segments[3]) }
        : second === "from-template" && third !== undefined
          ? { kind: "template" as const, templateId: third }
          : undefined;
    return <HostFlight from={from} sessionId={route.params.get("session") ?? undefined} />;
  }
  if (first === "join") {
    return (
      <JoinFlight
        key={second ?? ""}
        code={second !== undefined ? second.toLowerCase() : undefined}
      />
    );
  }
  if (first === "flight" && second !== undefined) {
    return (
      <FlightScreen
        code={second.toLowerCase()}
        sessionId={route.params.get("session") ?? undefined}
        unlinked={route.params.get("unlinked") === "1"}
      />
    );
  }

  return <Journal />;
}
